import type { AlgoliaCompanyRecord } from '../../types/company-search-record';
import { parseBestCompaniesHubApiPayload } from './parse-best-companies-hub-api-payload';

export interface FetchBestCompaniesHubFilters {
  /** Exact `industryEn` match. */
  industry?: string;
  /** Employer score ≥60 and recent hiring pace ≤1.5/mo — mirrors utils/best-companies/is-network-worthy. */
  networkWorthy?: boolean;
  /** Free-text company-name search — hits the full index rather than just the top-N by score. */
  query?: string;
  /**
   * Force the wider batch even with no other filter set: used by the hub's size/remote-friendly
   * chips, which filter client-side (Typesense can't express either as a filter_by clause) and so
   * need a bigger candidate pool than the default top-30-by-score batch to show every real match.
   */
  expand?: boolean;
}

const inflightByKey = new Map<string, Promise<AlgoliaCompanyRecord[]>>();

function keyFor(filters?: FetchBestCompaniesHubFilters): string {
  if (!filters) return '';
  return `${filters.industry ?? ''}|${filters.networkWorthy ? '1' : ''}|${filters.query ?? ''}|${filters.expand ? '1' : ''}`;
}

async function fetchBestCompaniesHubFromApiOnce(
  filters?: FetchBestCompaniesHubFilters,
): Promise<AlgoliaCompanyRecord[]> {
  try {
    const params = new URLSearchParams();
    if (filters?.industry) params.set('industry', filters.industry);
    if (filters?.networkWorthy) params.set('networkWorthy', '1');
    if (filters?.query) params.set('q', filters.query);
    if (filters?.expand) params.set('expand', '1');
    const qs = params.toString();
    const res = await fetch(`/api/best-companies-hub${qs ? `?${qs}` : ''}`, {
      credentials: 'same-origin',
      headers: { accept: 'application/json' },
    });
    if (!res.ok) return [];
    const json: unknown = await res.json();
    return parseBestCompaniesHubApiPayload(json);
  } catch {
    return [];
  }
}

/**
 * Loads hub rankings in the browser — with no `filters`, when Vike did not pass SSR data (e.g.
 * client-side navigation); with `filters`, to fetch past the default top-N batch so the hub's
 * industry/network-worthy chip filters show every match, not just whichever ones happened to
 * land in the top N by score. Concurrent callers for the same filter combo share one request.
 */
export async function fetchBestCompaniesHubFromApi(
  filters?: FetchBestCompaniesHubFilters,
): Promise<AlgoliaCompanyRecord[]> {
  const key = keyFor(filters);
  let inflight = inflightByKey.get(key);
  if (!inflight) {
    inflight = fetchBestCompaniesHubFromApiOnce(filters).finally(() => {
      inflightByKey.delete(key);
    });
    inflightByKey.set(key, inflight);
  }
  return inflight;
}

/**
 * Start loading hub data early (e.g. hover on a link to `/best-companies`) — dedupes with the page hook.
 */
export function prefetchBestCompaniesHubFromApi(): void {
  if (typeof window === 'undefined') return;
  void fetchBestCompaniesHubFromApi();
}
